import { useEffect, useRef } from 'react';

const services = [
  {
    icon: 'ri-home-4-line',
    title: 'Residential Inspections',
    description:
      'Comprehensive evaluations for buyers, sellers, and homeowners—covering structure, systems, and safety from foundation to roof.',
    tags: ['Buyers', 'Sellers', 'Homeowners'],
  },
  {
    icon: 'ri-building-2-line',
    title: 'Commercial Inspections',
    description:
      'Property condition assessments for investors, lenders, and business owners. Thorough evaluations of commercial buildings and multi-unit properties.',
    tags: ['Investors', 'Lenders', 'Business Owners'],
  },
  {
    icon: 'ri-test-tube-line',
    title: 'Environmental Testing',
    description:
      'Radon, mold, asbestos, and lead-based paint testing. Protect your family and investment with certified environmental assessments.',
    tags: ['Radon', 'Mold', 'Asbestos', 'Lead'],
  },
  {
    icon: 'ri-tools-line',
    title: 'Specialty Inspections',
    description:
      'Targeted evaluations for specific systems and components—roof, HVAC, electrical, plumbing, foundation, and sewer scope.',
    tags: ['Roof', 'HVAC', 'Plumbing', 'Foundation'],
  },
];

export default function Services() {
  const handleNav = (href: string) => {
    const el = document.querySelector(href);
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  const sectionRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const cards = sectionRef.current?.querySelectorAll('.service-card');
    if (!cards) return;
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            (entry.target as HTMLElement).style.opacity = '1';
            (entry.target as HTMLElement).style.transform = 'translateY(0)';
          }
        });
      },
      { threshold: 0.1 }
    );
    cards.forEach((card, i) => {
      (card as HTMLElement).style.opacity = '0';
      (card as HTMLElement).style.transform = 'translateY(24px)';
      (card as HTMLElement).style.transition = `opacity 0.6s ease ${i * 0.1}s, transform 0.6s ease ${i * 0.1}s`;
      observer.observe(card);
    });
    return () => observer.disconnect();
  }, []);

  return (
    <section id="services" ref={sectionRef} className="py-24 md:py-32 bg-white">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        {/* Header */}
        <div className="max-w-2xl mb-16">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-8 h-px bg-[#C9A961]"></div>
            <span className="text-[#C9A961] text-xs font-semibold uppercase tracking-[0.2em]">What We Offer</span>
          </div>
          <h2
            className="text-4xl md:text-5xl font-bold text-[#1A1A1A] leading-tight"
            style={{ fontFamily: "'Playfair Display', serif" }}
          >
            Inspection Services
          </h2>
          <p className="text-[#1A1A1A]/55 text-lg mt-4 leading-relaxed">
            From single-family homes to large commercial properties, our certified inspectors deliver thorough, reliable evaluations you can act on.
          </p>
        </div>

        {/* Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {services.map((svc, i) => (
            <article
              key={i}
              className="service-card group bg-[#F9F8F6] rounded-xl p-7 hover:bg-[#1A1A1A] transition-colors duration-400 cursor-default"
            >
              <div className="w-12 h-12 flex items-center justify-center bg-[#C9A961]/12 rounded-lg mb-6 group-hover:bg-[#C9A961]/20 transition-colors duration-300">
                <i className={`${svc.icon} text-[#C9A961] text-xl`}></i>
              </div>
              <h3
                className="text-lg font-bold text-[#1A1A1A] mb-3 group-hover:text-white transition-colors duration-300"
                style={{ fontFamily: "'Playfair Display', serif" }}
              >
                {svc.title}
              </h3>
              <p className="text-sm text-[#1A1A1A]/60 leading-relaxed mb-5 group-hover:text-white/65 transition-colors duration-300">
                {svc.description}
              </p>
              <div className="flex flex-wrap gap-1.5">
                {svc.tags.map((tag) => (
                  <span
                    key={tag}
                    className="text-xs px-2.5 py-1 rounded-full bg-[#C9A961]/10 text-[#C9A961] font-medium whitespace-nowrap group-hover:bg-[#C9A961]/20 transition-colors duration-300"
                  >
                    {tag}
                  </span>
                ))}
              </div>
            </article>
          ))}
        </div>

        {/* CTA */}
        <div className="mt-14 flex flex-col sm:flex-row items-start sm:items-center gap-5">
          <button
            onClick={() => handleNav('#contact')}
            className="bg-[#C9A961] text-white font-semibold text-sm px-8 py-3.5 rounded-md hover:bg-[#b8944f] transition-colors duration-200 cursor-pointer whitespace-nowrap"
          >
            Get Inspection Pricing
          </button>
          <a
            href="tel:+12814217355"
            className="flex items-center gap-2 text-sm font-semibold text-[#1A1A1A]/70 hover:text-[#C9A961] transition-colors whitespace-nowrap"
          >
            <div className="w-4 h-4 flex items-center justify-center">
              <i className="ri-phone-line text-[#C9A961] text-xs"></i>
            </div>
            Or call (281) 421-7355
          </a>
        </div>
      </div>
    </section>
  );
}
